import { BusinessObjectBase } from './base';
import { conditionWatcherSchema } from '@schemas/business-objects/condition-watcher';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { ConditionWatcherStatus, Id } from '../base-types';

export interface ConditionWatcherData {
  readonly id: Id;
  readonly boundaryEventId: string;
  readonly conditionExpression: string;
  readonly status?: ConditionWatcherStatus;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ConditionWatcher extends Readonly<ConditionWatcherData> {}
type ConditionWatcherCreateWithChildrenData = Partial<ConditionWatcherData>;

export class ConditionWatcher extends BusinessObjectBase {
  static readonly entityName = 'ConditionWatcher' as const;
  static readonly plural = 'condition-watchers';
  static schema = conditionWatcherSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ConditionWatcher[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ConditionWatcher(data));
  }

  static async findOne(filter?: any): Promise<ConditionWatcher | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ConditionWatcher(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ConditionWatcher> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ConditionWatcher(data);
  }

  static async create(
    data: Omit<ConditionWatcherData, 'id'>,
  ): Promise<ConditionWatcher> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ConditionWatcher(result);
  }

  static async createMany(
    data: Omit<ConditionWatcherData, 'id'>[],
  ): Promise<ConditionWatcher[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ConditionWatcher(item));
  }

  static async createWithChildren(
    data: ConditionWatcherCreateWithChildrenData,
  ): Promise<ConditionWatcher> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ConditionWatcher(result);
  }

  static async createManyWithChildren(
    data: ConditionWatcherCreateWithChildrenData[],
  ): Promise<ConditionWatcher[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ConditionWatcher(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ConditionWatcherData>,
  ): Promise<ConditionWatcher> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ConditionWatcher(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
